import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.beneficio-real-restaurante-cuanto-puedo-sacar.${field}`

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        href: 'https://sede.agenciatributaria.gob.es/static_files/Sede/Tema/IVA/IVA_reperc/Tipos_IVA_2026_26_02_2026.pdf',
        labelKey: 'sources.1',
        fallback: 'Agencia Tributaria, tipos impositivos en el IVA 2026, actualizado el 26 de febrero de 2026',
    },
    {
        href: 'https://www.hosteleriamadrid.com/blog/fiscal/cuanto-es-el-iva-en-hosteleria-que-debes-saber/',
        labelKey: 'sources.2',
        fallback: 'Hostelería de Madrid, los cuatro tipos de IVA aplicables en hostelería',
    },
    {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2023-8119',
        labelKey: 'sources.3',
        fallback: 'Orden HFP/311/2023, de 28 de marzo, límite exento de garantía en aplazamientos, 50.000 €',
    },
    {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2003-23186',
        labelKey: 'sources.4',
        fallback: 'Ley 58/2003, General Tributaria, artículo 28, recargos del período ejecutivo',
    },
    {
        href: 'https://www.ocu.org/dinero/renta-impuestos/noticias/interes-legal-demora',
        labelKey: 'sources.5',
        fallback: 'OCU, interés legal y de demora en 2026, publicado el 21 de abril de 2026',
    },
    {
        labelKey: 'sources.6',
        fallback:
            'Tipos del Impuesto sobre Sociedades 2026 y régimen transitorio de la Ley 7/2024, vía análisis publicado por Wolters Kluwer',
    },
    {
        labelKey: 'sources.7',
        fallback: 'Porcentaje y plazos del modelo 130, vía guía publicada por Wolters Kluwer',
    },
    {
        labelKey: 'sources.8',
        fallback:
            'Ejemplo numérico de marzo: elaboración propia de Tipi a partir de una estructura de costes de bar de barrio (no es un dato de mercado de un local concreto)',
    },
]

export function BeneficioRealArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'mano', label: t(k('toc.1'), '1. Cuando el cajón engaña') },
        { id: 'cinco-lineas', label: t(k('toc.2'), '2. Las cinco líneas del mes') },
        { id: 'no-tuyo', label: t(k('toc.3'), '3. Por qué el resultado no es dinero que puedas sacar') },
        { id: 'iva', label: t(k('toc.4'), '4. El IVA en tránsito') },
        { id: 'marzo', label: t(k('toc.5'), '5. Marzo con números') },
        { id: 'semana', label: t(k('toc.6'), '6. Tres números antes de tocar la caja') },
        { id: 'matices', label: t(k('toc.7'), '7. Lo que sigue sin estar cerrado') },
    ]

    const formulaRows = [
        {
            concept: t(k('table.formula.row.1.concept'), 'Ingresos'),
            what: t(k('table.formula.row.1.what'), 'La venta del mes, sin el IVA'),
        },
        {
            concept: t(k('table.formula.row.2.concept'), 'Compras'),
            what: t(k('table.formula.row.2.what'), 'El género consumido, sin el IVA'),
        },
        {
            concept: t(k('table.formula.row.3.concept'), 'Costes laborales'),
            what: t(
                k('table.formula.row.3.what'),
                'Salarios brutos + seguridad social de empresa + tu cuota de autónomo',
            ),
        },
        {
            concept: t(k('table.formula.row.4.concept'), 'Costes fijos'),
            what: t(k('table.formula.row.4.what'), 'Alquiler, suministros, seguros, gestoría, software, licencias'),
        },
        {
            concept: t(k('table.formula.row.5.concept'), 'Costes financieros'),
            what: t(k('table.formula.row.5.what'), 'Intereses del préstamo, comisiones de TPV, gastos de banco'),
        },
    ]

    const marchRows = [
        {
            label: t(k('table.marzo.row.1.label'), 'Caja del mes con IVA'),
            value: t(k('table.marzo.row.1.value'), '33.000 €'),
            strong: false,
        },
        {
            label: t(k('table.marzo.row.2.label'), 'Ingresos sin IVA'),
            value: t(k('table.marzo.row.2.value'), '30.000 €'),
            strong: true,
        },
        {
            label: t(k('table.marzo.row.3.label'), 'Género consumido'),
            value: t(k('table.marzo.row.3.value'), '−10.500 €'),
            strong: false,
        },
        {
            label: t(k('table.marzo.row.4.label'), 'Personal (bruto + SS empresa)'),
            value: t(k('table.marzo.row.4.value'), '−9.800 €'),
            strong: false,
        },
        {
            label: t(k('table.marzo.row.5.label'), 'Cuota de autónomo'),
            value: t(k('table.marzo.row.5.value'), '−400 €'),
            strong: false,
        },
        {
            label: t(k('table.marzo.row.6.label'), 'Costes fijos'),
            value: t(k('table.marzo.row.6.value'), '−4.300 €'),
            strong: false,
        },
        {
            label: t(k('table.marzo.row.7.label'), 'Costes financieros'),
            value: t(k('table.marzo.row.7.value'), '−250 €'),
            strong: false,
        },
        {
            label: t(k('table.marzo.row.8.label'), 'Resultado del mes'),
            value: t(k('table.marzo.row.8.value'), '4.750 €'),
            strong: true,
            total: true,
        },
    ]

    const weekItems = [
        t(
            k('week.1'),
            'Monta las cinco líneas del mes pasado con género consumido (inventario), no con la suma de facturas.',
        ),
        t(
            k('week.2'),
            'Separa en el banco el IVA en tránsito y la reserva del modelo 130 o del Impuesto sobre Sociedades. Fuera de la vista del cajón.',
        ),
        t(
            k('week.3'),
            'Pide a tu gestoría el desglose de la cuota del préstamo entre interés y principal. El principal sale de caja y no está en el resultado.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Un bar con 4.750 € de resultado en marzo solo puede sacar 2.900 €. Si sumas el IVA en tránsito, ves 6.550 € que parecen suyos. Menos de la mitad lo son.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Hostelero contando el dinero de la caja y revisando números en una mesa del bar tras el servicio',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <nav className={articleUi.toc} aria-label={t(k('tocAria'), 'Índice de contenidos')}>
                    <p className={articleUi.tocLabel}>{t(k('tocLabel'), 'Índice')}</p>
                    <ol className={articleUi.tocList}>
                        {tocItems.map(item => (
                            <li key={item.id}>
                                <a href={`#${item.id}`}>{item.label}</a>
                            </li>
                        ))}
                    </ol>
                </nav>

                <p className={cn(articleUi.p, 'mx-auto max-w-article-prose text-article-muted-soft')}>
                    {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                </p>

                <h2 id='mano' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.mano'), 'Cuando el cajón engaña')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.mano.1'),
                            'Te llega el modelo 130 o el aviso de la gestoría y el número no cuadra con lo que creías que era tuyo. Habías mirado el cajón, no el resultado.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.mano.2'),
                            'Coger dinero cuando «hay» no es el fallo. El fallo es no saber si ese dinero era tuyo o estaba de paso: IVA, impuesto sobre el beneficio, principal del préstamo.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('p.mano.3'), 'El agujero no aparece el día que sacas. Aparece el día que toca pagar.')}
                    </p>
                </div>

                <h2 id='cinco-lineas' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.cincoLineas'), 'Las cinco líneas del mes')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.cinco.1'), 'El cálculo del mes son cinco líneas. Todas sin IVA.')}
                    </p>
                </div>
                <div className={articleUi.tableWrap}>
                    <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                        <thead>
                            <tr>
                                <th>{t(k('table.formula.th.1'), 'Concepto')}</th>
                                <th>{t(k('table.formula.th.2'), 'Qué metes')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {formulaRows.map(row => (
                                <tr key={row.concept}>
                                    <td>
                                        <strong>{row.concept}</strong>
                                    </td>
                                    <td>{row.what}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>
                            {t(
                                k('p.cinco.2'),
                                'Los ingresos menos esas cuatro líneas de coste son el resultado del mes.',
                            )}
                        </strong>
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.cinco.3'),
                            'Ese número dice si el negocio ha ganado. No dice cuánto puedes sacar. Son dos números distintos.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.cinco.4Before'), 'Aviso sobre compras: ')}
                        <strong>
                            {t(
                                k('p.cinco.4Strong'),
                                'el coste del mes no son las compras del mes, es lo que has consumido.',
                            )}
                        </strong>{' '}
                        {t(
                            k('p.cinco.4After'),
                            'Existencias del día 1, más compras, menos existencias del día 30. Si no, junio y julio te mienten.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('p.cinco.5Before'), 'Acción: ')}
                        <strong>
                            {t(
                                k('p.cinco.5Strong'),
                                'antes de seguir, anota el resultado del mes pasado con consumo real.',
                            )}
                        </strong>
                    </p>
                </div>

                <h2 id='no-tuyo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.noTuyo'), 'Por qué el resultado no es dinero que puedas sacar')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.noTuyo.1'),
                            'Hay tres salidas de cuenta que no aparecen (o no enteras) en el resultado.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>{t(k('p.noTuyo.2Strong'), 'Uno: el impuesto sobre el beneficio.')}</strong>{' '}
                        {t(
                            k('p.noTuyo.2'),
                            'Si eres autónomo, cada trimestre adelantas con el modelo 130 el 20 % del rendimiento neto acumulado del año (plazos: días 1 a 20 de abril, julio y octubre, y del 1 al 30 de enero).',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.noTuyo.3'),
                            'Si tienes sociedad, en 2026 una micropyme con facturación inferior a un millón de euros paga el 19 % sobre los primeros 50.000 € de base y el 21 % sobre el resto. Una empresa de reducida dimensión paga el 23 %. Las de nueva creación, el 15 % los dos primeros ejercicios con beneficio.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>{t(k('p.noTuyo.4Strong'), 'Dos: la devolución del principal del préstamo.')}</strong>{' '}
                        {t(
                            k('p.noTuyo.4'),
                            'El interés es un coste y está en la fórmula. El principal no es un coste, pero sale de la cuenta todos los meses igual.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>{t(k('p.noTuyo.5Strong'), 'Tres: la inversión.')}</strong>{' '}
                        {t(
                            k('p.noTuyo.5'),
                            'Un horno de 9.000 € sale entero de tu caja este mes y contablemente se reparte en varios años. En el resultado casi no se nota. En el banco se nota entero.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('p.noTuyo.6Before'), 'Y a golpes: ')}
                        <strong>
                            {t(
                                k('p.noTuyo.6Strong'),
                                'las pagas extras y las vacaciones se ganan cada mes y se pagan en junio y en diciembre.',
                            )}
                        </strong>
                    </p>
                </div>

                <h2 id='iva' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.iva'), 'El IVA en tránsito')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.iva.1'), 'El IVA que cobras no es tuyo. Ni un euro.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.iva.2Before'),
                            'Entra en tu cuenta, se queda hasta tres meses y sale el día 20 del mes siguiente al cierre del trimestre. Pagas ',
                        )}
                        <strong>{t(k('p.iva.2Strong'), 'el repercutido menos el soportado en tus compras')}</strong>
                        {t(k('p.iva.2After'), '.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>{t(k('p.iva.3Strong'), 'El 10 % no es plano.')}</strong>{' '}
                        {t(
                            k('p.iva.3'),
                            'Todo lo que se consume en el local va al 10 %, vino y cerveza incluidos, porque vendes un servicio de hostelería.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.iva.4Before'), 'La botella para llevar es entrega de bienes y va al ')}
                        <strong>{t(k('p.iva.4Strong'), '21 %')}</strong>
                        {t(k('p.iva.4Mid'), ', según la ')}
                        <a
                            href='https://sede.agenciatributaria.gob.es/static_files/Sede/Tema/IVA/IVA_reperc/Tipos_IVA_2026_26_02_2026.pdf'
                            target='_blank'
                            rel='noopener noreferrer'
                            className={articleUi.link}
                        >
                            {t(k('p.iva.4Link'), 'tabla de tipos impositivos del IVA de 2026 de la Agencia Tributaria')}
                        </a>
                        {t(k('p.iva.4After'), ', actualizada el 26 de febrero de 2026.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.iva.5'),
                            'Si tienes take away y aplicas un 10 % plano a toda la venta, te descuadras el mes.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.iva.6Before'), 'Si te lo gastas y luego no llegas, puedes aplazar sin aval hasta ')}
                        <strong>{t(k('p.iva.6Strong'), '50.000 €')}</strong>
                        {t(k('p.iva.6Mid'), ' (')}
                        <a
                            href='https://www.boe.es/buscar/act.php?id=BOE-A-2023-8119'
                            target='_blank'
                            rel='noopener noreferrer'
                            className={articleUi.link}
                        >
                            {t(k('p.iva.6Link'), 'Orden HFP/311/2023')}
                        </a>
                        {t(k('p.iva.6After'), '). El interés de demora tributario está en el ')}
                        <strong>{t(k('p.iva.6Strong2'), '4,0625 %')}</strong>
                        {t(
                            k('p.iva.6End'),
                            ' en 2026 (tipo prorrogado). Si se te pasa el plazo: recargo del 5 %, 10 % o 20 % más intereses (art. 28 LGT).',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('p.iva.7Before'), 'Acción: ')}
                        <strong>
                            {t(k('p.iva.7Strong'), 'separa hoy en otra cuenta lo que sea IVA del trimestre en curso.')}
                        </strong>
                    </p>
                </div>

                <h2 id='marzo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.marzo'), 'Marzo con números')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.marzo.1'),
                            'Bar de barrio, autónomo, tres empleados, todo consumo en local. Cifras de ejemplo nuestras a partir de una estructura de costes típica de ese perfil; no son el dato de un local concreto ni un promedio de mercado.',
                        )}
                    </p>
                </div>
                <div className={articleUi.tableWrap}>
                    <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                        <thead>
                            <tr>
                                <th>{t(k('table.marzo.th.1'), 'Marzo')}</th>
                                <th>{t(k('table.marzo.th.2'), 'Importe')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {marchRows.map(row => (
                                <tr key={row.label} className={row.total ? articleUi.tableTotal : undefined}>
                                    <td>{row.strong || row.total ? <strong>{row.label}</strong> : row.label}</td>
                                    <td className='tabular-nums'>
                                        {row.strong || row.total ? <strong>{row.value}</strong> : row.value}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.marzo.2Before'), 'Del resultado hay que apartar el 20 % del modelo 130, ')}
                        <strong>{t(k('p.marzo.2Strong1'), '950 €')}</strong>
                        {t(k('p.marzo.2Mid'), ', y los ')}
                        <strong>{t(k('p.marzo.2Strong2'), '900 €')}</strong>{' '}
                        {t(k('p.marzo.2After'), 'de principal del préstamo que salen de la cuenta y no están arriba.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>{t(k('p.marzo.3'), 'Puedes sacar 2.900 €.')}</strong>
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.marzo.4'),
                            'En el banco hay además 1.800 € de IVA (3.000 € repercutidos menos 1.200 € soportados). Parecen tuyos hasta el 20 de abril.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>
                            {t(
                                k('p.marzo.5'),
                                'Resultado + IVA en tránsito: 6.550 € que parecen disponibles. Tuyos: 2.900 €. Menos de la mitad.',
                            )}
                        </strong>
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(
                            k('p.marzo.6Before'),
                            'Eso es el mes. Si el año ya venía flojo, sacar esos 2.900 € puede agrandar el agujero. El acumulado lo contamos aparte en ',
                        )}
                        <LearnArticleLink slug='beneficio-acumulado-cuanto-sacar-restaurante'>
                            {t(k('p.marzo.6Link'), 'cuánto puedes sacar cuando miras el año, no solo marzo')}
                        </LearnArticleLink>
                        {t(k('p.marzo.6After'), '.')}
                    </p>
                </div>

                <h2 id='semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.semana'), 'Tres números antes de tocar la caja')}
                </h2>
                <ol className={articleUi.numListGap2xl}>
                    {weekItems.map(item => (
                        <li key={item} className={articleUi.numItem}>
                            {item}
                        </li>
                    ))}
                </ol>

                <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                    {t(k('h2.matices'), 'Lo que sigue sin estar cerrado')}
                </h2>
                <div className={articleUi.prose}>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>
                            {t(
                                k('p.matices.1Strong'),
                                'Los tipos del Impuesto sobre Sociedades siguen en régimen transitorio.',
                            )}
                        </strong>{' '}
                        {t(
                            k('p.matices.1'),
                            'Bajan escalonadamente hasta 2029. Confírmalos con tu asesor antes de reservar.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        <strong>{t(k('p.matices.2Strong'), 'El 4,0625 % de demora es un tipo prorrogado')}</strong>
                        {t(k('p.matices.2'), ', no un tipo nuevo cerrado para 2026. Si hay Presupuestos, cambia.')}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.matices.3'),
                            'Las cifras de marzo son nuestras. Tu food cost, tu plantilla y tu deuda cambian el resultado; no copies el 2.900 € como si fuera el tuyo.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(k('p.matices.4Before'), 'Desde ')}
                        <TipiLink />
                        {t(
                            k('p.matices.4After'),
                            ' te decimos dónde se te va el margen. No tramitamos impuestos ni hacemos asesoría fiscal: reservas y modelos los cierras con tu gestoría.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGapMd)}>
                        {t(
                            k('p.matices.5'),
                            'Si este año has sacado de la caja y no sabes si podías, dínoslo y te decimos por dónde empezar.',
                        )}
                    </p>
                    <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                        {t(k('closing.signoff'), 'Nos vemos en la próxima, que de estas caen varias al mes.')}
                    </p>
                </div>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿El cajón y el resultado no te cuadran?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi ves el margen mes a mes. La parte fiscal la cierras con tu gestoría; nosotros te ayudamos a no mirar solo el saldo.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={articleUi.link}
                                    >
                                        {t(k(source.labelKey), source.fallback)}
                                    </a>
                                ) : (
                                    t(k(source.labelKey), source.fallback)
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
